import React, { useEffect, useState } from 'react';

import { FeaturedVideoHero, ComponentCta } from '@/types';

import { convertMarkdown, useIsMobile } from '@/ui/utils';
import { ComponentCtaButton, FeaturedVideo, Breadcrumb, Icon } from '@/ui/';
import { useRouter } from 'next/router';

import styles from './ComponentFeaturedVideoHero.module.scss';

const ComponentFeaturedVideoHero = ({
  heroHeading,
  heroSubheading,
  heroMedia,
  heroBody,
  ctas,
  showBreadcrumb = false,
  previewVideo,
  playWithoutSound,
  videoTitle,
  allowMiniPlayer,
}: FeaturedVideoHero) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const { locale = 'en' } = useRouter();
  const currentLocale = locale;

  const [isPlayingPreview, setIsPlayingPreview] = useState(true);
  const [isPlayingMain, setIsPlayingMain] = useState(false);

  const [isVideo, setIsVideo] = useState(false);
  const [videoDuration, setVideoDuration] = useState<string | null>(null);
  const hasMedia = heroMedia?.url;

  const isMobile = useIsMobile();

  const isImage = heroMedia?.contentType.startsWith('image');
  const hasCtas = !!ctas?.length;

  const togglePlayPause = () => {
    setIsPlayingPreview(false);
    setIsPlayingMain(true);
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    const video = !isImage;
    if (video) {
      setIsVideo(video);
    }
  }, [heroMedia, isImage]);

  useEffect(() => {
    if (isVideo && heroMedia?.url) {
      const video = document.createElement('video');
      video.src = heroMedia.url;
      video.onloadedmetadata = () => {
        const duration = Math.round(video.duration / 60);
        if (currentLocale === 'fr') {
          setVideoDuration(`${duration} minutes à regarder`);
        } else {
          setVideoDuration(`${duration} min watch`);
        }
      };
    }
  }, [isVideo, heroMedia]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );
      if (reducedMotion.matches) {
        setIsPlayingPreview(false);
      }
    }
  }, []);

  return (
    <div
      className={`featuredVideoHero ${styles.featuredVideoHero} ${
        !hasMedia ? styles['no-media'] : ''
      }`}
    >
      <div className={styles.featuredVideoHero__inner}>
        <div
          className={`${styles.featuredVideoHero__content} ${
            showBreadcrumb ? styles['breadcrumb'] : ''
          }`}
        >
          {showBreadcrumb && (
            <div className={styles.featuredVideoHero__breadcrumb}>
              <Breadcrumb usedOnLightBackground={false} />
            </div>
          )}

          <div className={styles.featuredVideoHero__contentInner}>
            <div className={styles['featuredVideoHero__content--left']}>
              <span className={styles.featuredVideoHero__subtitle}>
                {heroSubheading}
              </span>

              <h1 className={styles.featuredVideoHero__title}>{heroHeading}</h1>

              {heroBody && (
                <div className={styles.featuredVideoHero__description}>
                  {convertMarkdown(heroBody)}
                </div>
              )}
            </div>

            {hasCtas && (
              <div className={styles['featuredVideoHero__content--right']}>
                <div className={styles.featuredVideoHero__clickthroughs}>
                  {ctas.map((cta: ComponentCta) => {
                    return (
                      <ComponentCtaButton
                        key={cta.ctaText}
                        ctaType='link'
                        ctaText={cta.ctaText}
                        ctaAria={cta.ctaAria}
                        ctaStyle={cta.ctaStyle}
                        ctaIcon={cta.ctaIcon}
                        ctaIconPosition={cta.ctaIconPosition}
                        ctaUrl={cta.ctaUrl}
                        ctaPage={cta.ctaPage}
                        cssClass='dark-blue'
                        ctaMarketoFormId={cta.ctaMarketoFormId}
                        marketoSuccessMessage={cta.marketoSuccessMessage}
                        ctaDigitalAsset={cta.ctaDigitalAsset}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {isMobile && (
            <div className={styles.featuredVideoHero__captionWrapper}>
              <div className={styles.featuredVideoHero__caption}>
                {videoDuration && (
                  <>
                    <span>{videoDuration}</span>
                    {videoTitle}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div
          className={`${styles.featuredVideoHero__video} ${
            isPlaying && !playWithoutSound ? styles.playing : styles.stopped
          }`}
        >
          {isVideo && (
            <button
              type='button'
              className={`${styles.featuredVideoHero__playButton} ${
                isPlaying ? styles.pause : styles.play
              }`}
              onClick={togglePlayPause}
            >
              <Icon
                className={styles.featuredVideoHero__playIcon}
                id={isPlaying ? 'pause' : 'play'}
                width={isPlaying ? 30 : 40}
                height={isPlaying ? 30 : 40}
              />
            </button>
          )}
          <div
            className={`${styles.featuredVideoHero__videoWrapper} ${
              isPlayingMain ? styles.expanded : ''
            }`}
          >
            {isVideo && allowMiniPlayer && (
              <div className={'allowMiniPlayerRef'}></div>
            )}
            {isVideo && (
              <FeaturedVideo
                previewMedia={previewVideo}
                featuredMedia={heroMedia}
                isMuted={playWithoutSound}
                isPlaying={isPlaying}
                isPlayingPreview={isPlayingPreview}
                isPlayingMain={isPlayingMain}
                expanded={isPlayingMain}
                miniPlayer={allowMiniPlayer}
                onEnded={() => {
                  setIsPlaying(false);
                  setIsPlayingMain(false);
                  setIsPlayingPreview(true);
                }}
              />
            )}
          </div>
        </div>

        {!isMobile && (
          <div className={styles.featuredVideoHero__captionWrapper}>
            <div className={styles.featuredVideoHero__caption}>
              {videoDuration && (
                <>
                  <span>{videoDuration}</span>
                  {videoTitle}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentFeaturedVideoHero;
